import { api } from './config';
import {
  ApiResponse,
  AdminBrandCreateRequest,
  AdminBrandResponse,
  BrandStats,
  PageResponse
} from '../types';
import { adminFeedbackApi } from './feedbackFactory';
import { uploadFileToUrl } from './helpers';

export const adminApi = {
  // Sample requests
  getSampleRequests: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/admin/sample-requests', { params }),

  // Statistics — 응답 타입을 제네릭으로 명시하여 사용처에서 as any 우회 불필요.
  // (실제 페이로드 타입은 호출자가 import 하는 dashboard/statistics/types.ts 의 DimensionStats / TimeSeriesResponse)
  getSampleRequestStatistics: <TData = unknown>(excludeUnknown: boolean = false) =>
    api.get<ApiResponse<TData>>('/admin/statistics/sample-requests/dimensions', {
      params: { exclude_unknown: excludeUnknown },
    }),

  getSampleRequestTimeSeries: <TData = unknown>(params: {
    dimension: string;
    top_n?: number;
    months?: number;
    exclude_unknown?: boolean;
    include_others?: boolean;
  }) =>
    api.get<ApiResponse<TData>>('/admin/statistics/sample-requests/timeseries', { params }),

  getSampleRequestDetail: (id: string | number) =>
    api.get<ApiResponse>(`/admin/sample-requests/${id}`),

  updateSampleRequest: (id: string | number, data: Record<string, any>) =>
    api.put<ApiResponse>(`/admin/sample-requests/${id}`, data),

  // soft delete (휴지통 이동). 백엔드 DELETE /admin/sample-requests/{id} 가 @SQLDelete 로 soft delete 수행.
  deleteSampleRequest: (id: string | number) =>
    api.delete<ApiResponse>(`/admin/sample-requests/${id}`),

  assignManufacturer: (id: string | number, manufacturerId: number) =>
    api.post<ApiResponse>(`/admin/sample-requests/${id}/assign-manufacturer`, { manufacturer_id: manufacturerId }),

  getAllVersionsById: (id: string | number) =>
    api.get<ApiResponse>(`/admin/sample-requests/${id}/all-versions`),

  // Dashboard
  getDashboardStats: () => api.get<ApiResponse>('/admin/dashboard/stats'),
  getSystemStatus: () => api.get<ApiResponse>('/admin/system/status'),

  // Brands
  // 백엔드가 Page<BrandResponse> 반환 — 래퍼는 camelCase({ content, totalElements, totalPages }), content 항목은 Jackson 전역 snake_case(company_name 등).
  getBrands: (params?: Record<string, any>) =>
    api.get<ApiResponse<PageResponse<AdminBrandResponse>>>('/admin/brands', { params }),

  getBrandById: (id: number) =>
    api.get<ApiResponse<AdminBrandResponse>>(`/admin/brands/${id}`),

  getBrandStats: () =>
    api.get<ApiResponse<BrandStats>>('/admin/brands/stats'),

  createBrand: (data: AdminBrandCreateRequest) =>
    api.post<ApiResponse<AdminBrandResponse>>('/admin/brands', data),

  updateBrand: (id: number, data: Partial<AdminBrandCreateRequest>) =>
    api.put<ApiResponse<AdminBrandResponse>>(`/admin/brands/${id}`, data),

  deactivateBrand: (id: number) =>
    api.delete<ApiResponse>(`/admin/brands/${id}`),

  regenerateBrandCode: (id: number, verificationCode?: string) =>
    api.post<ApiResponse<AdminBrandResponse>>(`/admin/brands/${id}/regenerate-code`,
      verificationCode ? { verificationCode } : {}),

  // brandCode 기준 연관 request_code/project_code 최신화 (수동 트리거)
  syncBrandRequestCodes: (id: number) =>
    api.post<ApiResponse<{ updated_requests: number; updated_projects: number; brand_code: string }>>(
      `/admin/brands/${id}/sync-request-codes`),

  downloadBrandTemplate: () => api.get('/admin/brands/template', { responseType: 'blob' }),

  validateBrandImport: (file: File) => uploadFileToUrl('/admin/brands/import/validate', file),

  importBrands: (file: File) => uploadFileToUrl('/admin/brands/import', file),

  exportBrands: (params?: { keyword?: string }) =>
    api.get('/admin/brands/export', { params, responseType: 'blob' }),

  // Manufacturers
  getManufacturers: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/admin/manufacturers', { params }),

  downloadManufacturerTemplate: () =>
    api.get('/admin/manufacturers/template', { responseType: 'blob' }),

  validateManufacturerImport: (file: File) => uploadFileToUrl('/admin/manufacturers/import/validate', file),

  importManufacturers: (file: File) => uploadFileToUrl('/admin/manufacturers/import', file),

  validateSampleRequestImport: (file: File) => uploadFileToUrl('/admin/sample-requests/import/validate', file),

  importSampleRequests: (file: File, resolutions: string) =>
    uploadFileToUrl('/admin/sample-requests/import', file, { resolutions }),

  // Quotations
  getQuotations: (params?: Record<string, any>) =>
    api.get<ApiResponse>('/admin/quotations', { params }),

  downloadQuotationTemplate: () =>
    api.get('/admin/quotations/template', { responseType: 'blob' }),

  exportQuotations: (params?: { product?: string; package?: string }) =>
    api.get('/admin/quotations/export', { params, responseType: 'blob' }),

  importQuotations: (file: File) => uploadFileToUrl('/admin/quotations/import', file),

  bulkCreateQuotations: (quotations: any[]) =>
    api.post<ApiResponse>('/admin/quotations/bulk', { quotations }),

  // Email
  getEmailConfig: () => api.get<ApiResponse>('/admin/email/config'),

  sendEmail: (data: { to: string; subject: string; content: string; template?: string }) =>
    api.post<ApiResponse>('/admin/email/send', data),

  // Feedback (using factory pattern)
  ...adminFeedbackApi,

  // Admin-specific feedback methods
  getFeedbackStats: () =>
    api.get<ApiResponse>('/admin/feedback/stats'),

  updateFeedbackStatus: (id: number, data: { status: string; adminNotes?: string }) =>
    api.patch<ApiResponse>(`/admin/feedback/${id}/status`, data),

  // Projects (Admin access to brand projects)
  // 전체 프로젝트 단일 호출 - brand 별 N+1 호출 (getProjectsFlatByBrand 루프) 대체
  getAllProjects: () =>
    api.get<ApiResponse>('/admin/projects/all'),

  getProjectsByBrand: (brandId: number) =>
    api.get<ApiResponse>(`/admin/projects/brand/${brandId}/tree`),

  getActiveProjectsByBrand: (brandId: number) =>
    api.get<ApiResponse>(`/admin/projects/brand/${brandId}/tree/active`),

  getProjectsFlatByBrand: (brandId: number) =>
    api.get<ApiResponse>(`/admin/projects/brand/${brandId}/flat`),

  getProjectById: (projectId: number) =>
    api.get<ApiResponse>(`/admin/projects/${projectId}`),

  createProject: (brandId: number, data: { name: string; description?: string; targetMarketCountry?: string; parentId?: number }) =>
    api.post<ApiResponse>(`/admin/projects/brand/${brandId}`, data),

  updateProject: (projectId: number, data: {
    name?: string;
    description?: string;
    targetMarketCountry?: string;
    assignedAdminIds?: number[];
    status?: 'active' | 'completed' | 'archived';
  }) =>
    api.put<ApiResponse>(`/admin/projects/${projectId}`, data),

  // 여러 프로젝트 status 일괄 변경 (bulk edit modal)
  bulkUpdateProjectStatus: (projectIds: number[], status: 'active' | 'completed' | 'archived') =>
    api.post<ApiResponse<{ updatedCount: number }>>('/admin/projects/bulk-status', { projectIds, status }),

  // 여러 프로젝트 일괄 삭제 (hard delete, 자식 있는 건 skip)
  bulkDeleteProjects: (projectIds: number[]) =>
    api.post<ApiResponse<{ deletedCount: number; failedCount: number; failedIds: number[] }>>(
      '/admin/projects/bulk-delete', { projectIds }),

  deleteProject: (projectId: number) =>
    api.delete<ApiResponse>(`/admin/projects/${projectId}`),

  // Export project to Excel
  exportProjectToXlsx: (projectId: number) =>
    api.get(`/admin/projects/${projectId}/export/xlsx`, { responseType: 'blob' }),

  // Export sample requests by IDs (translate: 한글 번역 포함 여부, 기본 true)
  exportSampleRequests: (params?: { ids?: string; status?: string; keyword?: string; translate?: boolean }) =>
    api.get('/admin/sample-requests/export', { params, responseType: 'blob' }),

  // Project assignment (담당하기)
  toggleProjectAssignment: (projectId: number) =>
    api.post<ApiResponse>(`/admin/projects/${projectId}/assign`),

  isProjectAssigned: (projectId: number) =>
    api.get<ApiResponse<boolean>>(`/admin/projects/${projectId}/is-assigned`),

  getMyProjects: () =>
    api.get<ApiResponse>('/admin/my-projects'),

  getMyAssignmentStats: () =>
    api.get<ApiResponse>('/admin/my-assignments/stats'),

  // Bulk project assignment
  bulkAssignProjects: (projectIds: number[], adminId?: number) =>
    api.post<ApiResponse>('/admin/projects/bulk-assign', { projectIds, adminId }),

  // Reassign project to different admin (removes existing, adds new)
  reassignProject: (projectId: number, adminId: number | null) =>
    api.post<ApiResponse>(`/admin/projects/${projectId}/reassign`, { adminId }),

  // Add assignment to project (keeps existing)
  addProjectAssignment: (projectId: number, adminId?: number) =>
    api.post<ApiResponse>(`/admin/projects/${projectId}/add-assignment`, { adminId }),

  // Move request to project
  moveRequestToProject: (requestId: number, projectId: number) =>
    api.put<ApiResponse>(`/sample-requests/admin/${requestId}/move-to-project`, { project_id: projectId }),

  // Merge multiple projects into one
  mergeProjects: (sourceProjectIds: number[], newProjectName: string) =>
    api.post<ApiResponse>('/admin/projects/merge', { sourceProjectIds, newProjectName }),
};
